import { useState } from 'react'

import type { Schemas } from '@npc/api-client'
import { copyText } from '@npc/platform'

import { t } from '../../copy'
import { T } from '../../copy/text'
import { createRemoteAIInvitation, type RemoteAIInvitationBundle } from '../../app/remote-ai'

type Block = Schemas['MessageBlock']
type Duration = Schemas['RemoteAIInvitationCreate']['duration']

/**
 * 「发一张远程 AI 邀请」那张卡（arron 2026-09-10：
 * 「lulu 为什么没有这个能力呢，他应该可以直接分配平台的长期维护任务」）。
 *
 * # 卡是 Lulu 拟的，签发只由人点
 *
 * 服务端那件工具什么都不写：它只给出「给哪个项目、按什么名字签、
 * 什么厂商什么模型、多久」。**签发是红级动作**（改访问策略），
 * 所以这颗按钮走的是已有的 POST /projects/{id}/remote-ai/invitations，
 * 与「AI 与 API」那一屏同一条路、同一道权限。
 *
 * # 签发之后那两段为什么不回会话里
 *
 * invite_url 里带一次性 token，prompt 里也带。**消息是留档的**，
 * 把它们发回会话等于把一把 bearer secret 长期存着——服务端的块校验器
 * 连字段都不给它们。所以它们只活在这个组件的 state 里，刷新就没了，
 * 而那正是「只显示这一次」这句话的实话来源。
 */
export function isRemoteAIInviteCard(b: Block): boolean {
  return b.type === 'remote_ai_invite_card'
}

const DURATIONS = [
  ['30d', 'steward.remote_ai.duration_30d'],
  ['90d', 'steward.remote_ai.duration_90d'],
  ['180d', 'steward.remote_ai.duration_180d'],
  ['long_term', 'steward.remote_ai.duration_long_term'],
] as const

function text(b: Record<string, unknown>, key: string): string | undefined {
  const v = b[key]
  return typeof v === 'string' && v.trim() !== '' ? v : undefined
}

// durationLabel 把档位翻成摘要行里的那几个字。**用的是同一批模板**，
// 不另写一份——两份措辞迟早分家，而分家的表现是同一档在两处叫不同的名字。
function durationLabel(d: Duration): string {
  const found = DURATIONS.find(([value]) => value === d)
  return found ? t(found[1]) : d
}

function isDuration(v: string | undefined): v is Duration {
  return v === '30d' || v === '90d' || v === '180d' || v === 'long_term'
}

export function RemoteAIInviteCard({ block }: { block: Block }) {
  const raw = block as unknown as Record<string, unknown>
  const projectId = text(raw, 'project_id')
  const projectName = text(raw, 'project_name')
  const proposed = text(raw, 'duration')
  const [name, setName] = useState(text(raw, 'name') ?? '')
  const [provider, setProvider] = useState(text(raw, 'provider') ?? '')
  const [model, setModel] = useState(text(raw, 'model') ?? '')
  const [duration, setDuration] = useState<Duration>(isDuration(proposed) ? proposed : 'long_term')
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [issued, setIssued] = useState<RemoteAIInvitationBundle | null>(null)
  const [copied, setCopied] = useState<'link' | 'prompt' | null>(null)

  // 认不出的形状整块不画。渲染合同：客户端认不出就跳过它，body 照常显示。
  if (projectId === undefined || projectName === undefined) return null

  const issue = () => {
    if (busy || issued !== null) return
    setBusy(true)
    setFailed(false)
    void createRemoteAIInvitation(projectId, { name, provider, model, duration })
      .then((bundle) => setIssued(bundle))
      .catch(() => setFailed(true))
      .finally(() => setBusy(false))
  }

  return (
    <section
      data-testid="lulu-remote-ai-invite-card"
      data-project-id={projectId}
      data-state={issued === null ? 'proposed' : 'issued'}
      className="lu-brief-card mt-2"
    >
      <div className="lu-brief-meta">{projectName}</div>
      <p className="lu-brief-title m-0 mt-1.5">
        <T id="steward.remote_ai.card_title" />
      </p>
      <p className="lu-brief-meta m-0 mt-1">
        <T id="steward.remote_ai.card_hint" />
      </p>

      {issued === null ? (
        <>
          {/* **默认只问一颗按钮。** 卡一出来就摊开四个输入框，等于把「你确认一下」
              变成「你先填一张表」——而这四样平台已经替他想好了。要改的人点「改一下签给谁」。 */}
          <p data-testid="remote-ai-invite-summary" className="lu-brief-meta m-0 mt-2">
            {name} · {provider} / {model} · {durationLabel(duration)}
          </p>
          <div hidden={!editing} className="mt-2.5 grid gap-2">
            <label className="block">
              <span className="lu-invite-label">
                <T id="steward.remote_ai.field_name" />
              </span>
              <input
                data-testid="remote-ai-invite-name"
                className="lu-invite-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="lu-invite-label">
                  <T id="steward.remote_ai.field_provider" />
                </span>
                <input
                  data-testid="remote-ai-invite-provider"
                  className="lu-invite-input"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="lu-invite-label">
                  <T id="steward.remote_ai.field_model" />
                </span>
                <input
                  data-testid="remote-ai-invite-model"
                  className="lu-invite-input"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </label>
            </div>
            {/* 四档不能包在 label 里：button 是 labelable 元素，
                被 label 包住时四颗按钮会同时念成「管多久」，读屏的人分不出选的是哪一档。 */}
            <div>
              <span className="lu-invite-label">
                <T id="steward.remote_ai.field_duration" />
              </span>
              <div role="group" aria-label="管多久" className="flex flex-wrap gap-1.5">
                {DURATIONS.map(([value, copyId]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`remote-ai-invite-duration-${value}`}
                    aria-pressed={duration === value}
                    onClick={() => setDuration(value)}
                    className="lu-brief-ghost"
                    style={duration === value ? { borderColor: 'var(--lu-accent)' } : undefined}
                  >
                    <T id={copyId} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="remote-ai-invite-issue"
              disabled={busy || name.trim() === '' || provider.trim() === '' || model.trim() === ''}
              onClick={issue}
              className="lu-brief-primary"
            >
              <T id={busy ? 'steward.remote_ai.issuing' : 'steward.remote_ai.issue'} />
            </button>
            <button
              type="button"
              data-testid="remote-ai-invite-edit"
              aria-expanded={editing}
              onClick={() => setEditing((v) => !v)}
              className="lu-brief-ghost"
            >
              <T id="steward.remote_ai.edit" />
            </button>
            {failed ? (
              <span data-testid="remote-ai-invite-failed" className="text-npc-meta text-npc-lulu-bad">
                <T id="steward.remote_ai.failed" />
              </span>
            ) : null}
          </div>
        </>
      ) : (
        <div data-testid="remote-ai-invite-issued" className="mt-2.5">
          {/* **签完之后，那一整段就摆在对话里。**
              arron 2026-09-10：「lulu 应该在对话里直接发出来」。所以提示词排在最前、
              整段可见、复制是主按钮——他要的东西不该藏在第二次点击后面。
              而它**只活在这个组件的 state 里**：这一整段里带着一次性 invite_token，
              而消息是留档的（服务端的块校验器连字段都不给它）。 */}
          <p data-testid="remote-ai-invite-dont-close" className="lu-brief-meta m-0">
            <T id="steward.remote_ai.dont_close" />
          </p>
          <div className="mt-2">
            <span className="lu-invite-label">
              <T id="steward.remote_ai.prompt_label" />
            </span>
            <pre
              data-testid="remote-ai-invite-prompt"
              className="lu-invite-prompt max-h-[420px] overflow-auto whitespace-pre-wrap break-words"
            >
              {issued.prompt}
            </pre>
            <button
              type="button"
              data-testid="remote-ai-invite-copy-prompt"
              className="lu-brief-primary mt-2"
              onClick={() => {
                // 复制不成就不翻成「已复制」——按钮说的话必须是真的。
                void copyText(issued.prompt).then((r) => {
                  if (r.ok) setCopied('prompt')
                })
              }}
            >
              <T
                id={copied === 'prompt' ? 'steward.remote_ai.copy_prompt_done' : 'steward.remote_ai.copy_prompt'}
              />
            </button>
          </div>
          <div className="mt-2.5">
            <span className="lu-invite-label">
              <T id="steward.remote_ai.link_label" />
            </span>
            <div data-testid="remote-ai-invite-url" className="lu-invite-secret">
              {issued.invite_url}
            </div>
            <button
              type="button"
              data-testid="remote-ai-invite-copy-link"
              className="lu-brief-ghost mt-1.5"
              onClick={() => {
                void copyText(issued.invite_url).then((r) => {
                  if (r.ok) setCopied('link')
                })
              }}
            >
              <T id={copied === 'link' ? 'steward.remote_ai.copy_link_done' : 'steward.remote_ai.copy_link'} />
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
